/**
 * MGM-Automatisierung + Unteraufgaben — reine, testbare Logik (TES-481).
 *
 * Spiegelt die DB-Logik aus Migration 293 (add_business_days / compute_autodue_date /
 * apply_defect_automation / can_close_defect / v_defect_subtask_progress), damit Frontend
 * und Datenbank dasselbe Verhalten zeigen. KEINE Supabase-Calls hier — nur Pure Functions.
 *
 * SCOPE-DISZIPLIN: Auto-Assign-Conditions NUR severity + team_id; Auto-Due NUR Mon-Fri-Werktage
 * (kein Feiertagskalender). KEINE Enum-Erweiterung.
 */

export type DefectSeverity = "low" | "medium" | "high" | "critical";
export type DefectStatus = "open" | "in_progress" | "resolved" | "closed" | "cancelled";

/** Eine erledigte Unteraufgabe zaehlt bei resolved/closed; cancelled blockiert nicht. */
export const DONE_STATUSES: ReadonlySet<DefectStatus> = new Set<DefectStatus>([
  "resolved",
  "closed",
]);
/** Stati, die einen Parent NICHT mehr offen halten (= erledigt fuer all_done). Modul-intern. */
const TERMINAL_STATUSES: ReadonlySet<DefectStatus> = new Set<DefectStatus>([
  "resolved",
  "closed",
  "cancelled",
]);

// ---------------------------------------------------------------------------
// MGM-20 — Mon–Fri Werktags-Arithmetik
// ---------------------------------------------------------------------------

/** True wenn das Datum auf Samstag (6) oder Sonntag (0) faellt. Modul-intern. */
function isWeekend(date: Date): boolean {
  const dow = date.getDay(); // 0=Sun .. 6=Sat
  return dow === 0 || dow === 6;
}

/**
 * Addiert `businessDays` echte Mon–Fri-Werktage zur Basis (Wochenenden uebersprungen).
 * Spiegelt public.add_business_days(timestamptz, integer). businessDays <= 0 -> Basis unveraendert.
 * Arbeitet in lokaler Zeit (wie extract(dow) auf timestamptz im DB-Default-TZ).
 */
export function addBusinessDays(basis: Date, businessDays: number): Date {
  const result = new Date(basis.getTime());
  if (!Number.isFinite(businessDays) || businessDays <= 0) {
    return result;
  }
  let added = 0;
  const target = Math.floor(businessDays);
  while (added < target) {
    result.setDate(result.getDate() + 1);
    if (!isWeekend(result)) {
      added += 1;
    }
  }
  return result;
}

// ---------------------------------------------------------------------------
// MGM-19 — Auto-Assign-Regel-Aufloesung (Prioritaet + Condition-Match)
// ---------------------------------------------------------------------------

export interface AutoAssignCondition {
  severity?: DefectSeverity;
  team_id?: string;
}

export interface AutoAssignRule {
  id: string;
  priority: number;
  condition: AutoAssignCondition;
  target_user_id: string | null;
  target_team_id: string | null;
  is_active: boolean;
  /** ISO-Timestamp; Tie-Breaker bei gleicher priority (aeltere Regel gewinnt). */
  created_at: string;
}

/** Eingangs-Signale eines neuen Mangels, gegen die Conditions matchen. */
export interface DefectMatchInput {
  severity: DefectSeverity;
  team_id: string | null;
}

/**
 * Prueft, ob eine Condition (nur severity + team_id) auf den Mangel passt.
 * Leeres Condition-Objekt = Catch-all. Ein gesetztes Feld muss exakt matchen. Modul-intern;
 * ueber resolveAutoAssignRule getestet/konsumiert.
 */
function conditionMatches(condition: AutoAssignCondition, defect: DefectMatchInput): boolean {
  if (condition.severity !== undefined && condition.severity !== defect.severity) {
    return false;
  }
  if (condition.team_id !== undefined && condition.team_id !== defect.team_id) {
    return false;
  }
  return true;
}

/**
 * Waehlt die anzuwendende Regel: niedrigster `priority`-Wert gewinnt (1 vor 100),
 * bei Gleichstand die aeltere Regel (created_at). Nur aktive, matchende Regeln.
 * Kein Treffer -> null (kein Fehler; assigned_to bleibt unveraendert).
 * Deterministisch — spiegelt `ORDER BY priority ASC, created_at ASC LIMIT 1`.
 */
export function resolveAutoAssignRule(
  rules: readonly AutoAssignRule[],
  defect: DefectMatchInput,
): AutoAssignRule | null {
  const candidates = rules.filter((r) => r.is_active && conditionMatches(r.condition, defect));
  if (candidates.length === 0) return null;
  return candidates.reduce((best, current) => {
    if (current.priority !== best.priority) {
      return current.priority < best.priority ? current : best;
    }
    // Gleicher priority -> aeltere created_at gewinnt; stabil bei Gleichstand.
    return current.created_at < best.created_at ? current : best;
  });
}

// ---------------------------------------------------------------------------
// MGM-20 (TES-2237 US3) — Auto-Faelligkeits-Regel-Aufloesung + Client-Vorschau
//
// Spiegelt public.compute_autodue_date (Migration 293): exakter Team-Match vor global
// (team_id NULL). Genutzt fuer die "Vorschau: faellig am {date}"-Anzeige im Mangel-Erfassen-
// Dialog (US3) — rein informativ; die tatsaechliche Persistenz bleibt serverseitig (apply_defect_
// automation), AUSSER der Nutzer ueberschreibt die Vorschau manuell (dann wird der editierte Wert
// explizit mitgeschickt).
// ---------------------------------------------------------------------------

export interface AutoDueRule {
  id: string;
  severity: DefectSeverity;
  business_days: number;
  team_id: string | null;
  is_active: boolean;
}

/**
 * Waehlt die anzuwendende Faelligkeits-Regel fuer (severity, teamId): ein exakter Team-Match
 * gewinnt vor einer globalen Regel (team_id NULL). Kein Match -> null. Spiegelt die
 * `ORDER BY (r.team_id = _team_id) DESC NULLS LAST`-Praezedenz aus compute_autodue_date.
 * Modul-intern (nicht exportiert, anders als resolveAutoAssignRule): die einzige oeffentliche
 * Schnittstelle ist computeAutoDueDate — dort ueber das beobachtbare Datum getestet.
 */
function resolveAutoDueRule(
  rules: readonly AutoDueRule[],
  severity: DefectSeverity,
  teamId: string | null,
): AutoDueRule | null {
  const candidates = rules.filter((r) => r.is_active && r.severity === severity);
  const exactTeamMatch = teamId ? candidates.find((r) => r.team_id === teamId) : undefined;
  if (exactTeamMatch) return exactTeamMatch;
  return candidates.find((r) => r.team_id === null) ?? null;
}

/**
 * Client-seitige Vorschau der Auto-Faelligkeit (Mon-Fri Werktage ab `basis`, Default "jetzt").
 * Kein Match -> null (keine Vorschau, Formular zeigt kein Faelligkeitsfeld).
 *
 * Bekannte Grenzen (Copilot review, CHK PR #860 — bewusst akzeptiert, kein exakter Server-Mirror):
 * - Zeitzone: `addBusinessDays` rechnet in Browser-Lokalzeit (`getDay`/`setDate`), waehrend SQL
 *   `extract(dow FROM timestamptz)` in der DB-Session-Timezone auswertet — nahe einer
 *   Wochentag-/Datumsgrenze kann das Ergebnis um einen Werktag abweichen. Dieselbe Grenze gilt
 *   bereits fuer die bestehende Admin-Vorschau (DefectAutomationRules), die dieselbe Funktion
 *   nutzt; kein neues Risiko, nur ein neuer Konsument.
 * - RLS-Sichtbarkeit: `defect_autodue_rule`/`defect_autoassign_rule` sind ueber RLS auf
 *   team-sichtbare + globale Zeilen gescoped (Migration 293); reassignt ein Auto-Assign-Regel den
 *   Mangel auf ein Team, in dem der Ersteller KEIN Mitglied ist, kann dessen eigene Faelligkeits-
 *   regel fuer den Client unsichtbar sein — die Vorschau faellt dann auf global/kein-Match zurueck.
 * Da `apply_defect_automation` nach einem Insert ohnehin nie aufgerufen wird (TES-2297), ist auch
 * eine imperfekte Client-Vorschau strikt besser als der Status quo (dauerhaft NULL) — kein
 * Blocker fuer TES-2237, aber dokumentiert statt verschwiegen.
 */
export function computeAutoDueDate(
  rules: readonly AutoDueRule[],
  severity: DefectSeverity,
  teamId: string | null,
  basis: Date = new Date(),
): Date | null {
  const rule = resolveAutoDueRule(rules, severity, teamId);
  if (!rule) return null;
  return addBusinessDays(basis, rule.business_days);
}

// ---------------------------------------------------------------------------
// MGM-18 — Unteraufgaben-Fortschritt (x/y) + all_done
// ---------------------------------------------------------------------------

export interface SubtaskProgress {
  total: number;
  done: number;
  open: number;
  /** Es existiert >=1 Unteraufgabe UND keine ist mehr offen. Ohne Subtasks: false. */
  allDone: boolean;
}

/** Minimal-Shape einer Unteraufgabe fuer die Aggregation. */
export interface SubtaskStatusLike {
  status: DefectStatus;
}

/**
 * Aggregiert "x von y erledigt" ueber die Unteraufgaben eines Parents.
 * done = resolved+closed; open = alles ausser resolved/closed/cancelled.
 * Spiegelt v_defect_subtask_progress. Leere Liste -> total 0, allDone false.
 */
export function computeSubtaskProgress(subtasks: readonly SubtaskStatusLike[]): SubtaskProgress {
  const total = subtasks.length;
  let done = 0;
  let open = 0;
  for (const s of subtasks) {
    if (DONE_STATUSES.has(s.status)) {
      done += 1;
    } else if (!TERMINAL_STATUSES.has(s.status)) {
      open += 1;
    }
  }
  return { total, done, open, allDone: total > 0 && open === 0 };
}

/**
 * Schliesslogik (MGM-18): Ein Parent ist abschliessbar, wenn keine Unteraufgabe mehr offen ist.
 * Ohne Unteraufgaben immer true (keine Blockade). Spiegelt public.can_close_defect.
 */
export function canCloseDefect(subtasks: readonly SubtaskStatusLike[]): boolean {
  return subtasks.every((s) => TERMINAL_STATUSES.has(s.status));
}

// ---------------------------------------------------------------------------
// TES-1743 — Massnahmen-Fortschritt (x/y) + Abschluss-Gate
// ---------------------------------------------------------------------------

/** Eine Massnahme (defect_action) ist offen oder erledigt. */
export type ActionStatus = "open" | "done";

export interface ActionProgress {
  total: number;
  done: number;
  open: number;
  /** Es existiert >=1 Massnahme UND keine ist mehr offen. Ohne Massnahmen: false. */
  allDone: boolean;
}

/** Minimal-Shape einer Massnahme fuer die Aggregation. */
export interface ActionStatusLike {
  status: ActionStatus | string;
}

/**
 * Aggregiert "x von y erledigt" ueber die Massnahmen eines Mangels. Leere Liste -> total 0,
 * allDone false. Spiegelt v_defect_qs_progress (done/total) auf der Client-Seite.
 */
export function computeActionProgress(actions: readonly ActionStatusLike[]): ActionProgress {
  const total = actions.length;
  let done = 0;
  for (const a of actions) {
    if (a.status === "done") done += 1;
  }
  const open = total - done;
  return { total, done, open, allDone: total > 0 && open === 0 };
}

/**
 * Abschluss-Gate (TES-1743): ein Mangel ist massnahmen-seitig abschliessbar, wenn keine Massnahme
 * mehr offen ist. Ohne Massnahmen immer true (keine Blockade). Negiert public.defect_has_open_actions;
 * die HARTE Status-Durchsetzung lebt im Workflow-/Pruefungs-Gate (TES-1737).
 */
export function canCloseForActions(actions: readonly ActionStatusLike[]): boolean {
  return actions.every((a) => a.status === "done");
}
